import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  answerAt,
  buildExam,
  evaluate,
  examPlan,
  examSource,
  remainingS,
  type Exam,
  type ExamArea,
} from '@/core/exam'
import { cardFace } from '@/core/cards'
import { loadProgress, recordAnswer, saveProgress } from '@/core/progress'
import { gradeFor } from '@/core/srs'
import { BereichWahl } from './BereichWahl'
import { useSettings } from './settings'
import { subjectOfCard, type Subject } from './Viewer'

export interface ExamProps {
  /** Ohne laufenden Bogen gibt es nichts zu sehen, dann bleibt die Szene weg. */
  onSubject: (subject: Subject | undefined) => void
}


function clock(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * Prüfungsmodus: ein Bogen nach dem Bewertungsschlüssel, mit Uhr und ohne Auflösung
 * zwischendurch. Erst am Ende steht, was richtig war, denn in der Prüfung ist das auch so.
 */
export function Exam({ onSubject }: ExamProps) {
  const { t, pick, lang, settings } = useSettings()
  /**
   * Der Bereich kommt aus den Einstellungen und nicht aus einem eigenen Schalter.
   * Vorher führte dieser Modus seinen eigenen, und er stand immer wieder auf See: wer
   * im Katalog auf Binnen gestellt hatte, bekam hier ohne Hinweis den Seebogen.
   */
  const area: ExamArea = settings.bereich
  const [exam, setExam] = useState<Exam | undefined>()
  const [answers, setAnswers] = useState<Record<number, number>>({})
  const [at, setAt] = useState(0)
  const [done, setDone] = useState(false)
  const [left, setLeft] = useState(0)
  const saved = useRef(false)

  const plan = useMemo(() => examPlan(area), [area])
  const result = useMemo(() => (exam && done ? evaluate(exam, answers) : undefined), [exam, done, answers])
  const current = exam?.questions[at]

  useEffect(() => {
    onSubject(current && !done ? subjectOfCard(current.cardId) : undefined)
  }, [current, done, onSubject])

  // Die Uhr läuft in der Oberfläche, die Zeit selbst kommt aus dem Kern.
  useEffect(() => {
    if (!exam || done) return
    const tick = (): void => {
      const rest = remainingS(exam, Date.now())
      setLeft(rest)
      if (rest === 0) setDone(true)
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [exam, done])

  // Nach dem Durchgang bekommt der Lernstand jede Antwort mit, wie beim Quiz auch.
  useEffect(() => {
    if (!exam || !done || saved.current) return
    saved.current = true
    let progress = loadProgress()
    exam.questions.forEach((q, index) => {
      progress = recordAnswer(progress, q.cardId, gradeFor(answers[index] === q.question.correct, 20))
    })
    saveProgress(progress)
  }, [exam, done, answers])

  const start = useCallback(() => {
    saved.current = false
    setAnswers({})
    setAt(0)
    setDone(false)
    setExam(buildExam(plan, loadProgress(), Math.floor(Math.random() * 1_000_000), Date.now()))
  }, [plan])

  if (!exam) {
    return (
      <section className="exam" data-testid="exam">
        <h2>{t('exam.title')}</h2>
        <p className="detail__text">
          {t('exam.intro', {
            questions: plan.covered.reduce((n, p) => n + p.questions, 0),
            minutes: plan.variant.minutes,
          })}
        </p>
        <ul className="detail__quotes" data-testid="exam-plan">
          {plan.covered.map((part) => (
            <li key={part.name}>
              {t('exam.part', { name: part.name, questions: part.questions, min: part.minPoints ?? 0 })}
            </li>
          ))}
        </ul>
        {plan.skipped.length > 0 && (
          <p className="detail__text detail__note" data-testid="exam-skipped">
            {t('exam.skipped', {
              name: plan.skipped.map((p) => p.name).join(', '),
              questions: plan.skipped.reduce((n, p) => n + p.questions, 0),
            })}
          </p>
        )}
        {area === 'binnen' && (
          <p className="detail__text detail__note" data-testid="exam-binnen-note">
            {t('exam.binnen.note')}
          </p>
        )}
        <p className="signal__legend" data-testid="exam-source">
          {t('exam.source', { title: examSource.title, stand: examSource.stand ?? examSource.retrieved })}
        </p>
        <div className="signal__controls">
          <BereichWahl />
          <button type="button" className="signal__play" data-testid="exam-start" onClick={start}>
            {t('exam.start')}
          </button>
        </div>
      </section>
    )
  }

  if (done && result) {
    return (
      <section className="exam" data-testid="exam-result">
        <h2 data-testid="exam-verdict" data-passed={result.passed ? 'ja' : 'nein'}>
          {result.passed ? t('exam.passed') : t('exam.failed')}
        </h2>
        <ul className="detail__quotes">
          {result.parts.map((part) => (
            <li key={part.name} data-part={part.name} data-passed={part.passed ? 'ja' : 'nein'}>
              {t('exam.result.part', {
                name: part.name,
                points: part.points,
                questions: part.questions,
                min: part.minPoints ?? 0,
              })}
            </li>
          ))}
        </ul>
        <p className="detail__text" data-testid="exam-points">
          {t('exam.result.points', { points: result.points, questions: result.questions })}
        </p>
        <h3 className="detail__legend">{t('exam.citations')}</h3>
        {/* Der Modus behauptet, seine Fragen kämen aus dem eigenen, belegten Bestand.
            Hier steht, woher jede einzelne kam. Während des Durchgangs steht es nicht
            da: die Fundstelle verriete die Antwort. */}
        <ul className="detail__quotes" data-testid="exam-citations">
          {exam.questions.map((q) => (
            <li key={q.cardId} data-card={q.cardId}>
              <strong>{q.part}: </strong>
              {cardFace(q.cardId, lang).citation}
            </li>
          ))}
        </ul>
        <h3 className="detail__legend">{t('exam.wrong')}</h3>
        <ul className="detail__quotes" data-testid="exam-wrong">
          {exam.questions.map((q, index) =>
            answers[index] === q.question.correct ? null : (
              <li key={q.cardId}>
                {pick(q.question.prompt)} {pick(q.question.options[q.question.correct]!.label)}
              </li>
            ),
          )}
        </ul>
        <button type="button" className="toggle" data-testid="exam-again" onClick={() => setExam(undefined)}>
          {t('exam.again')}
        </button>
      </section>
    )
  }

  return (
    <section className="exam" data-testid="exam-running">
      <div className="exam__head">
        <span data-testid="exam-position">
          {t('exam.position', { at: at + 1, of: exam.questions.length, part: current!.part })}
        </span>
        <span data-testid="exam-clock">{clock(left)}</span>
      </div>
      <h2 className="quiz__prompt">{pick(current!.question.prompt)}</h2>
      <div className="quiz__options">
        {current!.question.options.map((option, index) => (
          <button
            key={option.id}
            type="button"
            className={`quiz__option${answers[at] === index ? ' quiz__option--on' : ''}`}
            data-option={option.id}
            onClick={() => setAnswers((old) => answerAt(exam, old, at, index, Date.now()))}
          >
            {pick(option.label)}
          </button>
        ))}
      </div>
      <div className="signal__controls">
        <button
          type="button"
          className="toggle"
          data-testid="exam-back"
          disabled={at === 0}
          onClick={() => setAt((n) => Math.max(0, n - 1))}
        >
          {t('exam.back')}
        </button>
        {at + 1 < exam.questions.length ? (
          <button type="button" className="signal__play" data-testid="exam-next" onClick={() => setAt((n) => n + 1)}>
            {t('exam.next')}
          </button>
        ) : (
          <button type="button" className="signal__play" data-testid="exam-finish" onClick={() => setDone(true)}>
            {t('exam.finish')}
          </button>
        )}
        <span className="signal__legend" data-testid="exam-open">
          {t('exam.open', { open: exam.questions.length - Object.keys(answers).length })}
        </span>
      </div>
    </section>
  )
}
